import { Model } from 'jms-domain'
import type { AnyDomainEntity } from 'jms-domain/src/__generated/model/index.js'
import { GrillBase, LynxWarmingDrawer } from 'jms-domain/src/__generated/model/index.js'

import type { StateUpdate } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../systems/configurator-interface.js'

/**
 * Handler of GrillBase variant toggle. When warming drawer variant is selected,
 * we also need to change door handing to none - to disable handles and instead
 * of two doors show just a plain panel.
 */
export const handleWarmingDrawerToggleTransformer = configuratorInterface.transformerBuilder.when
  .update(['modifyDomainEntity'])
  .fired.and(ctx => GrillBase.isId(ctx.update.entityId))
  .then.applySideEffect(ctx => {
    const entityId = ctx.update.entityId as Model.GrillBase['id']
    const delta = ctx.update.stateDelta as Partial<Model.GrillBase>

    const newState = ctx.getNewState()

    const newEntity = newState.entities.find(e => e.id === entityId) as Model.GrillBase | undefined
    if (!newEntity) {
      throw new Error(`GrillBase entity not found in new state: ${entityId}`)
    }

    const idSequence = ctx.getIdSequence()
    const updates: Array<StateUpdate<AnyDomainEntity, AnyDomainEntity>> = []

    let warmingDrawerId = newEntity.warmingDrawerId
    if (!warmingDrawerId && newEntity.variant === Model.GrillBaseVariant.WithWarmingDrawer) {
      warmingDrawerId = idSequence.nextId('LynxWarmingDrawer')

      updates.push(
        configuratorInterface.updates.DomainEntityCreation({
          entity: {
            id: warmingDrawerId,
            type: LynxWarmingDrawer.type,
            powderCoating: false,
          },
          parentId: entityId,
          updateTag: 'createWarmingDrawer',
        })
      )
    } else if (warmingDrawerId && newEntity.variant !== Model.GrillBaseVariant.WithWarmingDrawer) {
      updates.push(
        configuratorInterface.updates.EntityDeletion({
          entityId: warmingDrawerId,
          updateTag: 'deleteWarmingDrawer',
        })
      )

      warmingDrawerId = null
    }

    if (delta.variant || updates.length > 0) {
      return [
        ...updates,
        configuratorInterface.updates.DomainEntityModification({
          entityId,
          stateDelta: {
            handing: warmingDrawerId ? Model.Handing.none : Model.Handing.both,
            warmingDrawerId,
          },
          updateTag: 'handleWarmingDrawerToggle',
        }),
      ]
    }

    return []
  })
  .build('Handle GrillBase variant toggle')
